import { useMemo } from 'react';
import { parseWorkflowNodeData } from '@cotera/api';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { WORKFLOW_TRIGGER_UPSTREAM_SOURCE_ID } from '@cotera/utilities';
import {
  useWatchableValue,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { useAgentOptions } from '@cotera/client/app/v1/trigger/hooks';
import { useWorkflowModelOrNull } from '../workflow.model';
import { useWorkflowRunModelOrNull } from '../workflow-run.model';
import type { WorkflowGraphApiData } from '../workflow-graph.types';
import type { WorkflowRunGraph } from '../workflow-run.types';

const { workflowNodeFromGraphNode } = PersistedWorkflow;

/** The synthetic run-input node type (carries the trigger payload). */
const WORKFLOW_RUN_INPUT_NODE_TYPE = 'system/run-input';

/** Minimal node shape shared by the definition graph and the run graph. */
export type SourceLabelNode = {
  readonly id: string;
  readonly type: string;
  readonly data?: unknown;
  /** Run graph nodes expose a resolved display name (else humanized type). */
  readonly name?: string;
};

/** Friendly agent title for an agent node, else undefined. */
function agentTitleForNode(
  node: SourceLabelNode,
  agentTitleById: ReadonlyMap<string, string>
): string | undefined {
  if (node.type !== 'agent') {
    return undefined;
  }
  const parsed = parseWorkflowNodeData('agent', node.data);
  return parsed.t === 'valid'
    ? agentTitleById.get(parsed.data.agentId)
    : undefined;
}

/**
 * Maps each upstream ref key (the source node's stable id) to a friendly display
 * label — an agent's title, else the node's name. The trigger, referenced under
 * {@link WORKFLOW_TRIGGER_UPSTREAM_SOURCE_ID}, maps to the run-input node's name
 * (its trigger name). DISPLAY ONLY — refs resolve by id, this just renders them
 * with a human name. Entries equal to the raw key are omitted.
 */
export function buildNodeSourceLabels(
  nodes: readonly SourceLabelNode[],
  agentTitleById: ReadonlyMap<string, string>
): ReadonlyMap<string, string> {
  const labels = new Map<string, string>();
  for (const node of nodes) {
    if (node.type === WORKFLOW_RUN_INPUT_NODE_TYPE) {
      const triggerName = node.name ?? '';
      if (triggerName.trim() !== '') {
        labels.set(WORKFLOW_TRIGGER_UPSTREAM_SOURCE_ID, triggerName);
      }
      continue;
    }
    const label =
      agentTitleForNode(node, agentTitleById) ??
      workflowNodeFromGraphNode(node).name;
    if (label.trim() !== '' && label !== node.id) {
      labels.set(node.id, label);
    }
  }
  return labels;
}

/** Stable null watchables so the hook can run on any surface without crashing. */
const NULL_DEF_GRAPH = Watchable.from((): WorkflowGraphApiData | null => null);
const NULL_RUN_GRAPH = Watchable.from((): WorkflowRunGraph | null => null);

/** Agent-list titles keyed by agent id. */
export function useAgentTitleById(): ReadonlyMap<string, string> {
  const agents = useAgentOptions();
  return useMemo(() => {
    const map = new Map<string, string>();
    for (const agent of agents) {
      if (typeof agent.title === 'string' && agent.title.trim() !== '') {
        map.set(agent.id, agent.title);
      }
    }
    return map;
  }, [agents]);
}

/** Enabled trigger's display name from the definition graph, if any. */
function resolveTriggerName(graph: WorkflowGraphApiData): string | undefined {
  const binding =
    graph.triggerBindings.find((row) => row.enabled === true) ??
    graph.triggerBindings[0];
  if (binding === undefined) {
    return undefined;
  }
  const snapshot = graph.triggers.find(
    (row) => row.trigger.id === binding.triggerId
  );
  const name = snapshot?.trigger.name;
  return typeof name === 'string' && name.trim() !== '' ? name : undefined;
}

/**
 * Ref-key (source node id, or the trigger sentinel) → friendly-label map,
 * resolved from whichever graph is in scope: the editor's workflow model
 * (definition graph) OR the run model's run graph. Works on the editor canvas
 * AND run-detail surfaces; empty when neither is available.
 */
export function useUpstreamSourceLabels(): ReadonlyMap<string, string> {
  const model = useWorkflowModelOrNull();
  const runModel = useWorkflowRunModelOrNull();
  const defGraph = useWatchableValue(model?.graphWatchable ?? NULL_DEF_GRAPH);
  const runGraph = useWatchableValue(
    runModel?.graphWatchable ?? NULL_RUN_GRAPH
  );
  const agentTitleById = useAgentTitleById();
  return useMemo(() => {
    if (defGraph !== null) {
      const labels = new Map(
        buildNodeSourceLabels(defGraph.nodes, agentTitleById)
      );
      // The definition graph has no run-input node — add the trigger's name for
      // the sentinel so entry-node trigger refs still render friendly.
      const triggerName = resolveTriggerName(defGraph);
      if (triggerName !== undefined) {
        labels.set(WORKFLOW_TRIGGER_UPSTREAM_SOURCE_ID, triggerName);
      }
      return labels;
    }
    if (runGraph !== null) {
      return buildNodeSourceLabels(
        runGraph.nodes.map((n) => ({
          id: n.nodeId,
          type: n.type,
          data: n.data,
          name: n.name,
        })),
        agentTitleById
      );
    }
    return new Map<string, string>();
  }, [defGraph, runGraph, agentTitleById]);
}
